"use client";

import { MessageCircle, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "Is Neurotoxins only for treating existing wrinkles?",
    answer: "Not at all. While Neurotoxins is excellent for smoothing lines, it is also a powerful preventative tool. By relaxing specific muscles before deep creases form, we can maintain a smooth, youthful appearance and prevent the need for more invasive treatments later.",
    link: "Learn about preventative care"
  }, {
    question: "How does facial balancing differ from standard injections?",
    answer: "Facial balancing is an artistic approach that considers your unique proportions. Rather than just treating a single area, we use a combination of Neurotoxins and fillers to restore symmetry, enhance your natural features, and create a harmonious, refreshed look that feels uniquely yours.",
    link: "Explore our balancing philosophy"
  }, {
    question: "Will I look 'frozen' or unnatural?",
    answer: "Our goal is always refinement, not transformation. We prioritize a 'less is more' approach, ensuring you retain your natural expressions while simply softening the signs of fatigue or aging. You will look like a more rested, vibrant version of yourself.",
    link: "See our aesthetic approach"
  }, {
    question: "What is the benefit of a concierge aesthetic service?",
    answer: "Concierge care means privacy, convenience, and a personalized experience. We eliminate the stress of waiting rooms and traffic, providing high-quality, professional treatments in a serene, private environment tailored to your schedule and comfort.",
    link: "Discover the concierge difference"
  }, {
    question: "Are fillers safe for younger patients?",
    answer: "Yes, when performed by a board-certified expert. For younger patients, we use fillers strategically to enhance facial contours, refine proportions, or address early signs of volume loss. It is about subtle enhancement and long-term maintenance of your natural beauty.",
    link: "Read about filler safety"
  }, {
    question: "How do I know which treatments are right for me?",
    answer: "Every journey begins with a comprehensive consultation. Dr. Ojeda, DNP, APRN, FNP-C assesses your facial anatomy, listens to your goals, and creates a tailored plan that aligns with your vision. We never recommend unnecessary procedures.",
    link: "Book your consultation"
  }, {
    question: "What is the recovery time for these treatments?",
    answer: "Most of our treatments are 'lunchtime' procedures with minimal to no downtime. You can typically return to your daily activities immediately, making it easy to fit your self-care into a busy lifestyle without disruption.",
    link: "View treatment expectations"
  }, {
    question: "How does the medical oversight work?",
    answer: "Your safety is our highest priority. As a board-certified DNP, Dr. Ojeda, DNP, APRN, FNP-C works in close collaboration with our Medical Director to ensure every treatment meets the highest clinical standards of excellence and safety.",
    link: "Meet our clinical team"
  }];
  return <section data-section-id="14699" id="common-questions" className="bg-background text-foreground py-24 md:py-32 font-default">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-14">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-[720ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                Common questions
              </p>

              <h2 className="mt-5 font-serif font-normal text-foreground" style={{
              fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
              letterSpacing: "-0.01em",
              lineHeight: "1.2"
            }}>
                Refined{" "}
                <span className="relative inline-block italic">
                  answers
                  <svg className="absolute left-0 -bottom-1 w-full" height="8" viewBox="0 0 120 8" fill="none" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M2 5 Q 30 2, 60 4 T 118 4" stroke="hsl(15 45% 75%)" strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>
                </span>
                , for your peace of mind.
              </h2>

              <p className="mt-6 text-muted-foreground" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              lineHeight: "1.7"
            }}>
                We believe in transparency and education. Here are the most frequent inquiries from our clients regarding facial balancing and our concierge approach.
              </p>

              <div className="mt-10 rounded-lg border border-border bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-accent">
                    <MessageCircle className="h-6 w-6" style={{
                    color: "hsl(15 45% 75%)"
                  }} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      Still have questions?
                    </p>
                    <Link to="/ContactPage" className="group mt-2 inline-flex items-center text-sm font-medium text-foreground transition-colors duration-200 hover:text-primary">
                      <span className="relative">
                        Contact our concierge
                        <span className="absolute left-0 -bottom-1 block h-[1.5px] w-full transition-all duration-200 group-hover:h-[2px]" style={{
                        backgroundColor: "hsl(15 45% 75%)"
                      }} />
                      </span>
                      <span className="ml-2 transition-transform duration-200 group-hover:translate-x-1">
                        →
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="flex flex-col gap-3" defaultValue="item-0">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="group rounded-lg border border-border bg-card px-6 transition-colors duration-300 hover:border-primary data-[state=open]:border-primary last:border-b">
                  <AccordionTrigger className="py-5 text-left text-base md:text-lg font-semibold text-foreground hover:no-underline [&>svg]:hidden">
                    <span className="flex-1 pr-4">{faq.question}</span>
                    <Plus className="h-5 w-5 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" style={{
                  color: "hsl(15 45% 75%)"
                }} strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pt-0">
                    <p className="text-muted-foreground" style={{
                  fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                  lineHeight: "1.7"
                }}>
                      {faq.answer}
                    </p>
                    <Link to="/ExperiencePage" className="group/link mt-4 inline-flex items-center text-sm font-medium text-foreground transition-colors duration-200 hover:text-primary">
                      <span className="relative">
                        {faq.link}
                        <span className="absolute left-0 -bottom-1 block h-[1.5px] w-full transition-all duration-200 group-hover/link:h-[2px]" style={{
                      backgroundColor: "hsl(15 45% 75%)"
                    }} />
                      </span>
                      <span className="ml-2 transition-transform duration-200 group-hover/link:translate-x-1">
                        →
                      </span>
                    </Link>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}